import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ArrowLeft, Save, Minus, Plus } from 'lucide-react';
import { format, addDays, differenceInDays } from 'date-fns';
import { reservationsApi, CreateReservationData, Room } from '../api/reservations';
import { roomsApi } from '../api/rooms';
import LoadingSpinner from '../components/common/LoadingSpinner';

const reservationSchema = z.object({
  reservationId: z.string().optional(),  // 선택적 예약 ID
  roomId: z.string().min(1, '객실을 선택하세요'),
  roomType: z.string().min(1, '객실 타입을 입력하세요'),
  guestName: z.string().default('고객'),
  guestPhone: z.string().optional(),
  checkinDate: z.string().min(1, '체크인 날짜를 선택하세요'),
  checkoutDate: z.string().min(1, '체크아웃 날짜를 선택하세요'),
  checkinTime: z.string().optional(),
  checkoutTime: z.string().optional(),
  sellingPrice: z.number().min(0).default(0),
  receivedPrice: z.number().min(0).default(0),
  platform: z.string().default('offline'),
  checkinMemo: z.string().optional(),
  checkoutMemo: z.string().optional(),
});

type ReservationFormData = z.infer<typeof reservationSchema>;

const platforms = ['offline', '야놀자', '여기어때', '에어비앤비', '부킹닷컴', '직접예약', '기타'];

// 체크인 시간 옵션 (10:00 ~ 18:00)
const checkinTimeOptions = Array.from({ length: 9 }, (_, i) => {
  const hour = 10 + i;
  return `${hour.toString().padStart(2, '0')}:00`;
});

// 체크아웃 시간 옵션 (10:00 ~ 14:00)
const checkoutTimeOptions = Array.from({ length: 5 }, (_, i) => {
  const hour = 10 + i;
  return `${hour.toString().padStart(2, '0')}:00`;
});

// 메모 정리 함수
const cleanupMemo = (memo: string): string => {
  return memo
    .replace(/,\s*,+/g, ',')
    .replace(/^[\s,]+/, '')
    .replace(/[\s,]+$/, '')
    .trim();
};

export default function CreateReservationPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const dateParam = searchParams.get('date');
  const [availableRooms, setAvailableRooms] = useState<Room[]>([]);

  const today = format(new Date(), 'yyyy-MM-dd');
  const defaultCheckinDate = dateParam || today;
  const defaultCheckoutDate = format(addDays(new Date(defaultCheckinDate), 1), 'yyyy-MM-dd');

  const { data: rooms = [], isLoading: roomsLoading } = useQuery({
    queryKey: ['rooms'],
    queryFn: roomsApi.getAllRooms,
  });

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<ReservationFormData>({
    resolver: zodResolver(reservationSchema),
    defaultValues: {
      reservationId: '',
      guestName: '고객',
      guestPhone: '',
      checkinDate: defaultCheckinDate,
      checkoutDate: defaultCheckoutDate,
      checkinTime: '15:00',
      checkoutTime: '11:00',
      sellingPrice: 0,
      receivedPrice: 0,
      platform: 'offline',
      checkinMemo: '(C/I=15:00)',
      checkoutMemo: '(C/O=11:00)',
    },
  });

  const selectedRoomId = watch('roomId');
  const checkinDate = watch('checkinDate');
  const checkoutDate = watch('checkoutDate');
  const checkinTime = watch('checkinTime');
  const checkoutTime = watch('checkoutTime');

  // Auto-fill room type when room is selected
  const selectedRoom = rooms?.find((r: Room) => r.roomId === selectedRoomId);
  useEffect(() => {
    if (selectedRoom) {
      setValue('roomType', selectedRoom.roomType);
    }
  }, [selectedRoom, setValue]);

  // Fetch available rooms when checkin date changes
  useEffect(() => {
    let isMounted = true;

    if (checkinDate) {
      reservationsApi.getAvailableRooms(checkinDate)
        .then(data => {
          if (isMounted) setAvailableRooms(data);
        })
        .catch(err => {
          console.error('Failed to fetch available rooms:', err);
          if (isMounted) setAvailableRooms([]);
        });
    }

    return () => { isMounted = false; };
  }, [checkinDate]);

  // Update checkout date when checkin date changes
  useEffect(() => {
    if (checkinDate) {
      const checkin = new Date(checkinDate);
      const checkout = new Date(checkoutDate);
      if (checkout <= checkin) {
        setValue('checkoutDate', format(addDays(checkin, 1), 'yyyy-MM-dd'));
      }
    }
  }, [checkinDate, checkoutDate, setValue]);

  // Update checkin memo when checkin time changes
  const handleCheckinTimeChange = (newTime: string) => {
    setValue('checkinTime', newTime);
    if (newTime) {
      let currentMemo = watch('checkinMemo') || '';
      if (currentMemo.includes('(C/I=')) {
        currentMemo = currentMemo.replace(/\(C\/I=\d{1,2}:\d{2}\)/, `(C/I=${newTime})`);
      } else if (currentMemo === '') {
        currentMemo = `(C/I=${newTime})`;
      } else {
        currentMemo = `(C/I=${newTime}), ${currentMemo}`;
      }
      setValue('checkinMemo', cleanupMemo(currentMemo));
    }
  };

  // Update checkout memo when checkout time changes
  const handleCheckoutTimeChange = (newTime: string) => {
    setValue('checkoutTime', newTime);
    if (newTime) {
      let currentMemo = watch('checkoutMemo') || '';
      if (currentMemo.includes('(C/O=')) {
        currentMemo = currentMemo.replace(/\(C\/O=\d{1,2}:\d{2}\)/, `(C/O=${newTime})`);
      } else if (currentMemo === '') {
        currentMemo = `(C/O=${newTime})`;
      } else {
        currentMemo = `(C/O=${newTime}), ${currentMemo}`;
      }
      setValue('checkoutMemo', cleanupMemo(currentMemo));
    }
  };

  // Calculate nights
  const nights = checkinDate && checkoutDate
    ? differenceInDays(new Date(checkoutDate), new Date(checkinDate))
    : 0;

  const createMutation = useMutation({
    mutationFn: reservationsApi.createReservation,
    onSuccess: () => {
      // 대시보드 및 캘린더 관련 캐시 무효화
      queryClient.invalidateQueries({ queryKey: ['activeReservations'] });
      queryClient.invalidateQueries({ queryKey: ['availableRooms'] });
      queryClient.invalidateQueries({ queryKey: ['cancelledReservations'] });
      queryClient.invalidateQueries({ queryKey: ['reservations'] });
      queryClient.invalidateQueries({ queryKey: ['calendar'] });
      navigate(`/dashboard?date=${checkinDate}`);
    },
  });

  const onSubmit = (data: ReservationFormData) => {
    if (!data.guestName || data.guestName.trim() === '') {
      data.guestName = '고객';
    }
    createMutation.mutate(data as CreateReservationData);
  };

  // Time increment/decrement handlers
  const handleTimeIncrement = (field: 'checkinTime' | 'checkoutTime') => {
    const options = field === 'checkinTime' ? checkinTimeOptions : checkoutTimeOptions;
    const currentValue = watch(field) || options[0];
    const currentIndex = options.indexOf(currentValue);
    if (currentIndex < options.length - 1) {
      const newTime = options[currentIndex + 1];
      if (field === 'checkinTime') {
        handleCheckinTimeChange(newTime);
      } else {
        handleCheckoutTimeChange(newTime);
      }
    }
  };

  const handleTimeDecrement = (field: 'checkinTime' | 'checkoutTime') => {
    const options = field === 'checkinTime' ? checkinTimeOptions : checkoutTimeOptions;
    const currentValue = watch(field) || options[0];
    const currentIndex = options.indexOf(currentValue);
    if (currentIndex > 0) {
      const newTime = options[currentIndex - 1];
      if (field === 'checkinTime') {
        handleCheckinTimeChange(newTime);
      } else {
        handleCheckoutTimeChange(newTime);
      }
    }
  };

  // Sort and group rooms (same as EditReservationPage)
  const sortedRooms = useMemo(() => {
    return [...rooms]
      .filter((r: Room) => r.status === 'available' && r.roomId !== 'none')
      .sort((a: Room, b: Room) => {
        const typeCompare = (a.roomType || '').localeCompare(b.roomType || '');
        if (typeCompare !== 0) return typeCompare;
        return (a.roomId || '').localeCompare(b.roomId || '');
      });
  }, [rooms]);

  const groupedRooms = useMemo(() => {
    return sortedRooms.reduce((acc, room) => {
      const type = room.roomType || 'Other';
      if (!acc[type]) {
        acc[type] = [];
      }
      acc[type].push(room);
      return acc;
    }, {} as Record<string, Room[]>);
  }, [sortedRooms]);

  const roomTypes = useMemo(() => {
    return Object.keys(groupedRooms).sort((a, b) => a.localeCompare(b));
  }, [groupedRooms]);

  if (roomsLoading) {
    return <LoadingSpinner className="py-16" />;
  }

  return (
    <div className="space-y-6 max-w-2xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-2xl font-bold text-gray-900">예약 생성</h1>
        </div>
        <button
          type="submit"
          form="reservation-form"
          disabled={createMutation.isPending}
          className="btn btn-primary flex items-center"
        >
          {createMutation.isPending ? (
            <LoadingSpinner size="sm" className="mr-2" />
          ) : (
            <Save className="w-5 h-5 mr-2" />
          )}
          저장
        </button>
      </div>

      {createMutation.isError && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {(createMutation.error as any)?.response?.data?.message || '예약 생성에 실패했습니다. 다시 시도해주세요.'}
        </div>
      )}

      <form id="reservation-form" onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="card">
          <h2 className="text-lg font-semibold mb-4">새 예약 입력</h2>

          {/* 예약 ID (선택사항) */}
          <div className="mb-4">
            <label className="label">예약 ID (선택사항)</label>
            <input
              {...register('reservationId')}
              className="input"
              placeholder="비워두면 자동 생성됩니다"
            />
            <p className="mt-1 text-xs text-gray-500">
              입력 시 해당 ID로 생성, 미입력 시 자동 생성
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* 이름 */}
            <div>
              <label className="label">이름</label>
              <input
                {...register('guestName')}
                className="input"
                placeholder="고객"
              />
            </div>

            {/* 연락처 */}
            <div>
              <label className="label">연락처</label>
              <input
                {...register('guestPhone')}
                className="input"
                placeholder="01000000000"
              />
            </div>

            {/* 체크인 날짜 */}
            <div>
              <label className="label">체크인 날짜 *</label>
              <input
                {...register('checkinDate')}
                type="date"
                className="input"
              />
              {errors.checkinDate && (
                <p className="mt-1 text-sm text-red-500">{errors.checkinDate.message}</p>
              )}
            </div>

            {/* 체크아웃 날짜 */}
            <div>
              <label className="label">체크아웃 날짜 *</label>
              <input
                {...register('checkoutDate')}
                type="date"
                className="input"
              />
              {nights > 0 && (
                <p className="mt-1 text-sm text-gray-500">
                  {nights}박 {nights + 1}일
                </p>
              )}
              {nights <= 0 && checkinDate && checkoutDate && (
                <p className="mt-1 text-sm text-red-500">
                  체크아웃은 체크인 이후여야 합니다
                </p>
              )}
              {errors.checkoutDate && (
                <p className="mt-1 text-sm text-red-500">{errors.checkoutDate.message}</p>
              )}
            </div>

            {/* 체크인 시간 */}
            <div>
              <label className="label">체크인 시간</label>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => handleTimeDecrement('checkinTime')}
                  className="btn btn-secondary p-2"
                >
                  <Minus className="w-4 h-4" />
                </button>
                <select
                  value={checkinTime}
                  onChange={(e) => handleCheckinTimeChange(e.target.value)}
                  className="input flex-1"
                >
                  {checkinTimeOptions.map((time) => (
                    <option key={time} value={time}>{time}</option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => handleTimeIncrement('checkinTime')}
                  className="btn btn-secondary p-2"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* 체크아웃 시간 */}
            <div>
              <label className="label">체크아웃 시간</label>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => handleTimeDecrement('checkoutTime')}
                  className="btn btn-secondary p-2"
                >
                  <Minus className="w-4 h-4" />
                </button>
                <select
                  value={checkoutTime}
                  onChange={(e) => handleCheckoutTimeChange(e.target.value)}
                  className="input flex-1"
                >
                  {checkoutTimeOptions.map((time) => (
                    <option key={time} value={time}>{time}</option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => handleTimeIncrement('checkoutTime')}
                  className="btn btn-secondary p-2"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* 객실 */}
            <div>
              <label className="label">객실 ID *</label>
              <select {...register('roomId')} className="input">
                <option value="">객실 선택</option>
                {roomTypes.map((type) => (
                  <optgroup key={type} label={type}>
                    {groupedRooms[type].map((room: Room) => {
                      const isAvailable = availableRooms.some(r => r.roomId === room.roomId);
                      return (
                        <option key={room.roomId} value={room.roomId}>
                          {isAvailable ? '* ' : ''}{room.roomId.toUpperCase()} - {room.roomAddress?.toUpperCase()}
                        </option>
                      );
                    })}
                  </optgroup>
                ))}
              </select>
              {errors.roomId && (
                <p className="mt-1 text-sm text-red-500">{errors.roomId.message}</p>
              )}
            </div>

            {/* 플랫폼 */}
            <div>
              <label className="label">플랫폼</label>
              <select {...register('platform')} className="input">
                {platforms.map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </div>

            {/* 가격 */}
            <div>
              <label className="label">판매가</label>
              <input
                {...register('sellingPrice', { valueAsNumber: true })}
                type="number"
                min="0"
                className="input"
                placeholder="0"
              />
            </div>

            {/* 입금가 */}
            <div>
              <label className="label">입금가</label>
              <input
                {...register('receivedPrice', { valueAsNumber: true })}
                type="number"
                min="0"
                className="input"
                placeholder="0"
              />
            </div>

            {/* 체크인 메모 */}
            <div>
              <label className="label">체크인 메모</label>
              <textarea
                {...register('checkinMemo')}
                className="input min-h-[80px]"
                placeholder=""
              />
            </div>

            {/* 체크아웃 메모 */}
            <div>
              <label className="label">체크아웃 메모</label>
              <textarea
                {...register('checkoutMemo')}
                className="input min-h-[80px]"
                placeholder=""
              />
            </div>
          </div>
        </div>

        {/* Hidden room type field */}
        <input type="hidden" {...register('roomType')} />
      </form>
    </div>
  );
}
